import React, { useEffect, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  Button,
  Chip,
  Rating,
  useTheme,
  alpha,
} from "@mui/material";
import {
  ArrowBack,
  DirectionsCar,
  Speed,
  LocalGasStation,
  AttachMoney,
  Verified,
  Security,
  Build,
  Description,
  CheckCircle,
  Star,
  Handyman,
  ReceiptLong,
} from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import { useRequestState } from "@app/hooks";
import { selectFindByProductIdState } from "../selector";
import { productFindById } from "../slice";
import { PATH_DASHBOARD } from "@app/routes/paths";
import { Strings } from "@app/constants";

// ----------------------------------------------------------------------
// Helper Formatters
// ----------------------------------------------------------------------
const formatDate = (dateString?: string | null) => {
  if (!dateString) return "N/A";
  const d = dayjs(dateString);
  if (!d.isValid()) return String(dateString);
  return d.format("DD MMM YYYY");
};

const formatPrice = (price?: number | string | null) => {
  if (price === undefined || price === null || price === "") return "N/A";
  const num = Number(price);
  if (isNaN(num)) return String(price);
  return num.toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  });
};

const ProducDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);

  const locationState = location.state as { productId?: number } | undefined;
  const searchParams = new URLSearchParams(location.search);
  const productId = locationState?.productId || searchParams.get("id");

  const { data: productData, loading } = useRequestState({
    stateSelector: selectFindByProductIdState,
    errorShown: true,
  });

  useEffect(() => {
    if (productId) {
      dispatch(productFindById({ productId: Number(productId) }));
    }
  }, [dispatch, productId]);

  const car = productData?.car || productData || {};
  const fontFamily = Strings.fontLanguage.workSans || "'Work Sans', sans-serif";

  // Compute Car Status
  const isSold =
    car?.isSold === true ||
    car?.sold === true ||
    car?.status?.toLowerCase() === "sold" ||
    car?.isSoldCar === true ||
    Boolean(car?.soldCar) ||
    Boolean(car?.soldAt);

  const isAvailableForSale =
    car?.carAvailableForSell === true ||
    car?.isAvailableForSell === true ||
    car?.isAvailable === true ||
    car?.status?.toLowerCase() === "available" ||
    car?.status?.toLowerCase() === "available for sale" ||
    car?.status?.toLowerCase() === "available for sell" ||
    car?.carAvailableForSell === "true" ||
    car?.isAvailableForSell === "true";

  const carStatus = isSold
    ? "Sold"
    : isAvailableForSale
    ? "Available for Sale"
    : "Not Available for Sale";

  // Normalize Images
  const rawImages = car?.images || [];
  const images: Array<{ url: string; label?: string }> = [];

  const imageLabels = [
    "Front View",
    "Back View",
    "Left Side View",
    "Right Side View",
    "Interior Dashboard",
    "Odometer Reading",
  ];

  if (Array.isArray(rawImages)) {
    rawImages.forEach((img: any, idx: number) => {
      if (typeof img === "string" && img) {
        images.push({ url: img, label: imageLabels[idx] || `View ${idx + 1}` });
      } else if (img && typeof img === "object" && img.url) {
        images.push({
          url: img.url,
          label: img.label || imageLabels[idx] || `View ${idx + 1}`,
        });
      }
    });
  }

  // Fallback views from individual fields if present
  [
    { key: "frontView", label: "Front View" },
    { key: "backView", label: "Back View" },
    { key: "leftSideView", label: "Left Side View" },
    { key: "rightSideView", label: "Right Side View" },
    { key: "interiorDashboard", label: "Interior Dashboard" },
    { key: "odometerReading", label: "Odometer Reading" },
  ].forEach((v) => {
    if (car[v.key] && typeof car[v.key] === "string" && !images.some((i) => i.url === car[v.key])) {
      images.push({ url: car[v.key], label: v.label });
    }
  });

  // Car Title
  const brandName = car?.brand?.name || (typeof car?.brand === "string" ? car?.brand : "") || "";
  const modelName = car?.model?.name || (typeof car?.model === "string" ? car?.model : "") || "";
  const carTitle = [brandName, modelName].filter(Boolean).join(" ") || `Car #${car?.id || productId || ""}`;

  // Loading State
  if (loading && !car?.id) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "80vh",
          backgroundColor: "#F8F9FA",
        }}
      >
        <CircularProgress sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  // Info Row Component
  const DetailRow = ({
    label,
    value,
    icon,
    isBadge,
    badgeColor,
  }: {
    label: string;
    value: React.ReactNode;
    icon?: React.ReactNode;
    isBadge?: boolean;
    badgeColor?: "success" | "warning" | "error" | "default" | "primary";
  }) => (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      py={1.2}
      borderBottom={`1px solid ${theme.palette.divider}`}
      sx={{ "&:last-child": { borderBottom: "none" } }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        {icon && (
          <Box sx={{ color: theme.palette.text.secondary, display: "flex" }}>
            {icon}
          </Box>
        )}
        <Typography
          variant="body2"
          sx={{
            fontFamily,
            color: "#5F5F5F",
            fontWeight: 500,
          }}
        >
          {label}
        </Typography>
      </Stack>

      {isBadge ? (
        <Chip
          label={String(value)}
          size="small"
          color={badgeColor || "default"}
          sx={{ fontWeight: 600, fontSize: "0.78rem" }}
        />
      ) : (
        <Typography
          variant="body2"
          sx={{
            fontFamily,
            color: "#101828",
            fontWeight: 600,
            textAlign: "right",
          }}
        >
          {value || "N/A"}
        </Typography>
      )}
    </Stack>
  );

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        backgroundColor: "#F8F9FA",
        minHeight: "100vh",
        fontFamily,
      }}
    >
      {/* ------------------------------------------------------------- */}
      {/* HEADER SECTION */}
      {/* ------------------------------------------------------------- */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              backgroundColor: "#fff",
              border: "1px solid #E0E0E0",
              "&:hover": { backgroundColor: "#f5f5f5" },
            }}
          >
            <ArrowBack sx={{ fontSize: 20 }} />
          </IconButton>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "#101828",
                fontSize: { xs: "1.25rem", sm: "1.45rem" },
                fontFamily: "Poppins",
              }}
            >
              {carTitle}
            </Typography>

            {car?.manufacturingYear && (
              <Chip
                label={String(car.manufacturingYear)}
                size="small"
                sx={{
                  fontWeight: 600,
                  backgroundColor: theme.palette.grey[100],
                  color: theme.palette.text.primary,
                  fontFamily: "monospace",
                }}
              />
            )}

            <Chip
              label={carStatus}
              size="small"
              sx={{
                fontWeight: 600,
                ...(carStatus === "Available for Sale" && {
                  backgroundColor: theme.palette.success.lighter || "#E8F5E9",
                  color: theme.palette.success.main || "#2E7D32",
                }),
                ...(carStatus === "Not Available for Sale" && {
                  backgroundColor: theme.palette.warning.lighter || "#FFF3E0",
                  color: theme.palette.warning.main || "#E65100",
                }),
                ...(carStatus === "Sold" && {
                  backgroundColor: theme.palette.error.lighter || "#FFEBEE",
                  color: theme.palette.error.main || "#C62828",
                }),
              }}
            />
          </Stack>
        </Stack>
      </Box>

      {/* ------------------------------------------------------------- */}
      {/* 4 SUMMARY STATS CARDS */}
      {/* ------------------------------------------------------------- */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)",
            sm: "repeat(4, 1fr)",
          },
          gap: 2,
          mb: 3,
        }}
      >
        {/* Price Card */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            border: "1px solid #E0E0E0",
            backgroundColor: "#fff",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                width: 44,
                height: 44,
              }}
            >
              <AttachMoney />
            </Avatar>
            <Box>
              <Typography variant="caption" sx={{ color: "#757575", fontWeight: 500 }}>
                Car Price
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                {formatPrice(car?.price)}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Driven Card */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            border: "1px solid #E0E0E0",
            backgroundColor: "#fff",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              sx={{
                backgroundColor: alpha(theme.palette.info.main, 0.1),
                color: theme.palette.info.main,
                width: 44,
                height: 44,
              }}
            >
              <Speed />
            </Avatar>
            <Box>
              <Typography variant="caption" sx={{ color: "#757575", fontWeight: 500 }}>
                Kilometers Driven
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#101828" }}>
                {car?.driven ? `${car.driven} km` : "N/A"}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Engine / Fuel Card */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            border: "1px solid #E0E0E0",
            backgroundColor: "#fff",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              sx={{
                backgroundColor: alpha(theme.palette.success.main, 0.1),
                color: theme.palette.success.main,
                width: 44,
                height: 44,
              }}
            >
              <LocalGasStation />
            </Avatar>
            <Box>
              <Typography variant="caption" sx={{ color: "#757575", fontWeight: 500 }}>
                Fuel / Engine
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#101828" }}>
                {car?.engineType || "N/A"}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Rating Card */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            border: "1px solid #E0E0E0",
            backgroundColor: "#fff",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              sx={{
                backgroundColor: alpha(theme.palette.warning.main, 0.1),
                color: theme.palette.warning.main,
                width: 44,
                height: 44,
              }}
            >
              <Star />
            </Avatar>
            <Box>
              <Typography variant="caption" sx={{ color: "#757575", fontWeight: 500 }}>
                Rating & Reviews
              </Typography>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#101828" }}>
                  {car?.avgRating ? Number(car.avgRating).toFixed(1) : "0.0"}
                </Typography>
                <Rating
                  value={Number(car?.avgRating) || 0}
                  readOnly
                  precision={0.5}
                  size="small"
                />
              </Stack>
            </Box>
          </Stack>
        </Paper>
      </Box>

      {/* ------------------------------------------------------------- */}
      {/* IMAGES & GALLERY SECTION */}
      {/* ------------------------------------------------------------- */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 2,
          border: "1px solid #E0E0E0",
          backgroundColor: "#fff",
          mb: 3,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, mb: 2, fontFamily: "Poppins", color: "#101828" }}
        >
          Car Photos ({images.length})
        </Typography>

        {images.length > 0 ? (
          <Stack spacing={2}>
            {/* Main Active Image */}
            <Box
              sx={{
                width: "100%",
                height: { xs: 260, sm: 400 },
                borderRadius: 2,
                overflow: "hidden",
                backgroundColor: "#F5F5F5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
              }}
            >
              <Box
                component="img"
                src={images[selectedImageIndex]?.url}
                alt={images[selectedImageIndex]?.label || "Car Photo"}
                sx={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
              {images[selectedImageIndex]?.label && (
                <Chip
                  label={images[selectedImageIndex].label}
                  size="small"
                  sx={{
                    position: "absolute",
                    bottom: 12,
                    left: 12,
                    backgroundColor: "rgba(0,0,0,0.75)",
                    color: "white",
                    fontWeight: 600,
                  }}
                />
              )}
            </Box>

            {/* Thumbnail Strip */}
            <Stack
              direction="row"
              spacing={1.5}
              sx={{
                overflowX: "auto",
                pb: 0.5,
                "&::-webkit-scrollbar": { height: 6 },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#ccc",
                  borderRadius: 3,
                },
              }}
            >
              {images.map((img, idx) => (
                <Box
                  key={idx}
                  onClick={() => setSelectedImageIndex(idx)}
                  sx={{
                    minWidth: 100,
                    width: 100,
                    height: 75,
                    borderRadius: 1.5,
                    overflow: "hidden",
                    cursor: "pointer",
                    border:
                      selectedImageIndex === idx
                        ? `2.5px solid ${theme.palette.primary.main}`
                        : "1px solid #E0E0E0",
                    opacity: selectedImageIndex === idx ? 1 : 0.7,
                    transition: "all 0.2s",
                    "&:hover": { opacity: 1 },
                    backgroundColor: "#f5f5f5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    component="img"
                    src={img.url}
                    alt={img.label}
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </Box>
              ))}
            </Stack>
          </Stack>
        ) : (
          <Box
            sx={{
              py: 6,
              textAlign: "center",
              backgroundColor: "#F9FAFB",
              borderRadius: 2,
              border: "1px dashed #E0E0E0",
            }}
          >
            <DirectionsCar sx={{ fontSize: 48, color: "#9E9E9E", mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No photos uploaded for this car.
            </Typography>
          </Box>
        )}
      </Paper>

      {/* ------------------------------------------------------------- */}
      {/* DETAILED INFORMATION CARDS GRID */}
      {/* ------------------------------------------------------------- */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
          gap: 3,
        }}
      >
        {/* 1. General & Specifications */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 2,
            border: "1px solid #E0E0E0",
            backgroundColor: "#fff",
            height: "100%",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" mb={2}>
            <DirectionsCar sx={{ color: theme.palette.primary.main }} />
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, fontFamily: "Poppins", color: "#101828" }}
            >
              General Specifications
            </Typography>
          </Stack>

          <DetailRow label="Car ID" value={`#${car?.id || "N/A"}`} />
          <DetailRow label="Brand" value={brandName || "N/A"} />
          <DetailRow label="Model" value={modelName || "N/A"} />
          <DetailRow label="Variant" value={car?.variant || "N/A"} />
          <DetailRow
            label="Manufacturing Year"
            value={car?.manufacturingYear || "N/A"}
          />
          <DetailRow
            label="Category"
            value={car?.category?.name || (typeof car?.category === "string" ? car?.category : "N/A")}
          />
          <DetailRow label="Fuel / Engine Type" value={car?.engineType || "N/A"} />
          <DetailRow
            label="Transmission"
            value={car?.transmission || car?.transmissionType || "N/A"}
          />
          <DetailRow
            label="Color"
            value={
              <Stack direction="row" spacing={1} alignItems="center">
                {car?.color && (
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      backgroundColor: car.color.toLowerCase(),
                      border: "1px solid #ccc",
                      display: "inline-block",
                    }}
                  />
                )}
                <span>{car?.color || "N/A"}</span>
              </Stack>
            }
          />
          <DetailRow
            label="Registration / License Plate"
            value={
              car?.licensePlateNumber ||
              car?.carNumber ||
              car?.registrationNumber ||
              "N/A"
            }
          />
        </Paper>

        {/* 2. Legal & Registration */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 2,
            border: "1px solid #E0E0E0",
            backgroundColor: "#fff",
            height: "100%",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" mb={2}>
            <Verified sx={{ color: theme.palette.primary.main }} />
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, fontFamily: "Poppins", color: "#101828" }}
            >
              Legal & Documentation
            </Typography>
          </Stack>

          <DetailRow
            label="RC Available"
            value={
              car?.rcAvailable === true || car?.rcAvailable === "Yes"
                ? "Available"
                : "Not Available"
            }
            isBadge
            badgeColor={
              car?.rcAvailable === true || car?.rcAvailable === "Yes"
                ? "success"
                : "error"
            }
          />

          <DetailRow
            label="Loan & Hypothecation"
            value={
              car?.loanHypothecation === true || car?.loanHypothecation === "Yes"
                ? "Yes (Active Loan)"
                : "No (Clear Title)"
            }
            isBadge
            badgeColor={
              car?.loanHypothecation === true || car?.loanHypothecation === "Yes"
                ? "warning"
                : "success"
            }
          />

          <DetailRow
            label="Usage Type"
            value={car?.usageType || "Personal"}
          />

          <DetailRow
            label="Available For Sale"
            value={isAvailableForSale ? "Yes" : "No"}
            isBadge
            badgeColor={isAvailableForSale ? "success" : "default"}
          />

          <DetailRow
            label="Sold Status"
            value={isSold ? "Sold" : "Available"}
            isBadge
            badgeColor={isSold ? "error" : "success"}
          />
        </Paper>

        {/* 3. Insurance Details */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 2,
            border: "1px solid #E0E0E0",
            backgroundColor: "#fff",
            height: "100%",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" mb={2}>
            <Security sx={{ color: theme.palette.primary.main }} />
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, fontFamily: "Poppins", color: "#101828" }}
            >
              Insurance Details
            </Typography>
          </Stack>

          <DetailRow
            label="Insurance Status"
            value={
              car?.insuranceAvailable === true || car?.insuranceAvailable === "Yes"
                ? "Active"
                : "Not Available / Expired"
            }
            isBadge
            badgeColor={
              car?.insuranceAvailable === true || car?.insuranceAvailable === "Yes"
                ? "success"
                : "error"
            }
          />

          <DetailRow
            label="Insurance Type"
            value={car?.insuranceType || "N/A"}
          />

          <DetailRow
            label="Insurance Valid Till"
            value={formatDate(car?.insuranceValidTill || car?.expiryDate)}
          />

          <DetailRow
            label="Any Claims Made"
            value={
              car?.anyClaimsMade === true || car?.anyClaimsMade === "Yes"
                ? "Yes"
                : "No"
            }
            isBadge
            badgeColor={
              car?.anyClaimsMade === true || car?.anyClaimsMade === "Yes"
                ? "warning"
                : "success"
            }
          />
        </Paper>

        {/* 4. Condition & History */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 2,
            border: "1px solid #E0E0E0",
            backgroundColor: "#fff",
            height: "100%",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" mb={2}>
            <Build sx={{ color: theme.palette.primary.main }} />
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, fontFamily: "Poppins", color: "#101828" }}
            >
              Vehicle Condition & Service History
            </Typography>
          </Stack>

          <DetailRow
            label="Overall Condition"
            value={car?.carCondition || "Good"}
            isBadge
            badgeColor={
              car?.carCondition === "Excellent"
                ? "success"
                : car?.carCondition === "Needs Work"
                ? "error"
                : "primary"
            }
          />

          <DetailRow
            label="Accident History"
            value={car?.accidentHistory || "Accident Free"}
            isBadge
            badgeColor={
              car?.accidentHistory === "Accident Free"
                ? "success"
                : car?.accidentHistory === "Major"
                ? "error"
                : "warning"
            }
          />

          <DetailRow
            label="Full Service Record"
            value={
              car?.serviceHistory === true ||
              car?.fullServiceRecord === "Yes" ||
              car?.serviceHistory === "Yes"
                ? "Available"
                : "Not Available"
            }
            isBadge
            badgeColor={
              car?.serviceHistory === true ||
              car?.fullServiceRecord === "Yes" ||
              car?.serviceHistory === "Yes"
                ? "success"
                : "default"
            }
          />
        </Paper>

        {/* 5. Description & Notes */}
        {car?.description && (
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: "1px solid #E0E0E0",
              backgroundColor: "#fff",
              gridColumn: { xs: "span 1", md: "span 2" },
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
              <Description sx={{ color: theme.palette.primary.main }} />
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, fontFamily: "Poppins", color: "#101828" }}
              >
                Description & Highlights
              </Typography>
            </Stack>
            <Typography
              variant="body2"
              sx={{
                color: "#474747",
                lineHeight: 1.7,
                whiteSpace: "pre-line",
              }}
            >
              {car.description}
            </Typography>
          </Paper>
        )}

        {/* 6. Extra Information & Features */}
        {((Array.isArray(car?.extraInformation) && car?.extraInformation.length > 0) ||
          (Array.isArray(car?.extraInfo) && car?.extraInfo.length > 0) ||
          (Array.isArray(car?.specifications) && car?.specifications.length > 0)) && (
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: "1px solid #E0E0E0",
              backgroundColor: "#fff",
              gridColumn: { xs: "span 1", md: "span 2" },
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" mb={2}>
              <Handyman sx={{ color: theme.palette.primary.main }} />
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, fontFamily: "Poppins", color: "#101828" }}
              >
                Extra Features & Custom Specifications
              </Typography>
            </Stack>

            {/* Extra Info Badges */}
            {(Array.isArray(car?.extraInformation) || Array.isArray(car?.extraInfo)) && (
              <Box mb={2}>
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                  {(car?.extraInformation || car?.extraInfo || []).map(
                    (info: any, idx: number) => (
                      <Chip
                        key={idx}
                        icon={<CheckCircle style={{ fontSize: 16 }} />}
                        label={
                          typeof info === "string"
                            ? info
                            : info?.title || info?.name || JSON.stringify(info)
                        }
                        sx={{
                          backgroundColor: alpha(theme.palette.primary.main, 0.08),
                          color: theme.palette.primary.main,
                          fontWeight: 600,
                          borderRadius: 1,
                        }}
                      />
                    )
                  )}
                </Stack>
              </Box>
            )}

            {/* Specifications Key-Value */}
            {Array.isArray(car?.specifications) && car.specifications.length > 0 && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                  },
                  gap: 1.5,
                }}
              >
                {car.specifications.map((spec: any, idx: number) => (
                  <Box
                    key={idx}
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      backgroundColor: "#F9FAFB",
                      border: "1px solid #E0E0E0",
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "#757575", fontWeight: 500 }}>
                      {spec?.key || spec?.name || `Spec #${idx + 1}`}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#101828" }}>
                      {spec?.value || "N/A"}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        )}

        {/* 7. Sold Details (if Sold) */}
        {isSold && (
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: `1px solid ${theme.palette.error.light || "#FFCDD2"}`,
              backgroundColor: "#FFF8F8",
              gridColumn: { xs: "span 1", md: "span 2" },
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" mb={2}>
              <ReceiptLong sx={{ color: theme.palette.error.main }} />
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, fontFamily: "Poppins", color: theme.palette.error.main }}
              >
                Sale Transaction Details
              </Typography>
            </Stack>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(4, 1fr)",
                },
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Final Sale Price:
                </Typography>
                <Typography variant="body1" fontWeight={700} color="error.main">
                  {formatPrice(car?.soldCar?.finalPrice || car?.finalPrice || car?.price)}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Payment Method:
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {car?.soldCar?.paymentMethod || car?.paymentMethod || "N/A"}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Invoice Number:
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {car?.soldCar?.invoiceNumber || car?.invoiceNumber || "N/A"}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary">
                  Sold Date:
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {formatDate(car?.soldCar?.createdAt || car?.soldAt || car?.updatedAt)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}

        {/* 8. Rating & Customer Reviews */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 2,
            border: "1px solid #E0E0E0",
            backgroundColor: "#fff",
            gridColumn: { xs: "span 1", md: "span 2" },
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Star sx={{ color: "#FFD700" }} />
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, fontFamily: "Poppins", color: "#101828" }}
              >
                Customer Reviews ({car?.reviews?.length || 0})
              </Typography>
            </Stack>
          </Stack>

          {car?.reviews && car.reviews.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead sx={{ backgroundColor: "#F4F6F8" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: "#474747" }}>Customer</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#474747" }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#474747" }}>Rating</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#474747" }}>Review / Feedback</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {car.reviews.map((review: any, index: number) => (
                    <TableRow key={review?.id || index} hover>
                      <TableCell>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar
                            src={review?.user?.profileImage}
                            sx={{
                              width: 32,
                              height: 32,
                              fontSize: 13,
                              backgroundColor: theme.palette.primary.main,
                            }}
                          >
                            {review?.user?.firstName?.[0] || "U"}
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: "#101828" }}>
                            {[review?.user?.firstName, review?.user?.lastName]
                              .filter(Boolean)
                              .join(" ") || "Customer"}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(review?.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Rating
                          value={Number(review?.rating) || 0}
                          readOnly
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: "#474747" }}>
                          {review?.comment || "No comment provided."}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box
              sx={{
                py: 4,
                textAlign: "center",
                backgroundColor: "#F9FAFB",
                borderRadius: 1.5,
                border: "1px dashed #E0E0E0",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No reviews submitted yet for this car.
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default ProducDetail;
